/**
 * Toaster.js — bottom-of-screen toast stack reading from $toasts.
 *
 * Pre-#173 the codebase had `showToast` writing into the `$toasts` atom but
 * no component reading it, so toasts have been silently no-op'ing since the
 * v2 rewrite. Mounting this once at the App root brings ALL existing
 * showToast call sites to life — assignment alerts, send-error nudges,
 * "Note saved" confirms, etc.
 *
 * Why a separate component (vs. inlining in App):
 *   - Lives outside the screen switch, so toasts persist across navigation.
 *   - Tappable toasts (assignment notifications) need their own click area
 *     without depending on whatever screen is current.
 */

import { html }     from 'htm/preact';
import { useStore } from '@nanostores/preact';

import { $toasts, dismissToast } from '../stores/ui.js';

export function Toaster() {
  const toasts = useStore($toasts);
  if (!toasts.length) return null;

  return html`
    <div class="inbox-toaster" role="status" aria-live="polite">
      ${toasts.map(t => html`
        <${ToastItem} key=${t.id} toast=${t} />
      `)}
    </div>
  `;
}

function ToastItem({ toast }) {
  const clickable = !!toast.onClick;
  const handleClick = () => {
    if (!clickable) return;
    try { toast.onClick(); } catch (e) { console.warn('[v2 toast] onClick threw:', e?.message); }
    dismissToast(toast.id);
  };

  return html`
    <div
      class="inbox-toast inbox-toast-${toast.kind || 'info'}${clickable ? ' inbox-toast-clickable' : ''}"
      role=${clickable ? 'button' : 'status'}
      tabindex=${clickable ? '0' : null}
      onClick=${clickable ? handleClick : null}
      onKeyDown=${clickable
        ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(); } }
        : null}
    >
      <span class="inbox-toast-msg">${toast.message}</span>
      <button
        class="inbox-toast-dismiss"
        aria-label="Dismiss"
        onClick=${(e) => { e.stopPropagation(); dismissToast(toast.id); }}
      >×</button>
    </div>
  `;
}
